import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@core/components/ui/dialog";
import { LoginForm } from "@core/components/login-form";
import { DialogProps } from "@radix-ui/react-dialog";
import { Button } from "@core/components/ui/button";
import { cn } from "@core/lib/utils";
import { useTranslation } from "react-i18next";
import { dialog } from "@core/components/ui/use-dialog";

interface LoginModalProps extends DialogProps {
  asChild?: boolean;
  onClose?: () => void;
  skipSubmit?: boolean;
}

interface ShowLoginDialogOptions {
  onClose?: () => void;
  onSuccess?: () => void;
  skipSubmit?: boolean;
}

export const LoginModal = (props: LoginModalProps) => {
  const { onClose, asChild, children, skipSubmit, ...rest } = props;
  const [open, setOpen] = React.useState<boolean>(false);
  const { t } = useTranslation();

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} {...rest}>
      <DialogTrigger asChild className={cn("cursor-pointer")}>
        {asChild && children ? (
          children
        ) : (
          <Button type="button" variant="secondary">
            <span>{t("ui.signIn", "Sign In")}</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("ui.signIn", "Sign In")}</DialogTitle>
        </DialogHeader>
        <LoginForm onClose={() => handleOpenChange(false)} skipSubmit={skipSubmit}/>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Programmatic version of LoginModal - call this from any function
 * @example
 * import { showLoginDialog } from "@core/components/forms/LoginModal";
 * 
 * function MyComponent() {
 *   const handleClick = () => {
 *     showLoginDialog({
 *       onSuccess: () => console.log("User logged in!"),
 *       onClose: () => console.log("Dialog closed")
 *     });
 *   };
 * }
 */
export const showLoginDialog = (options?: ShowLoginDialogOptions) => {
  const dialogInstance = dialog({
    title: "Sign In",
    content: (
      <LoginForm 
        onClose={() => {
          dialogInstance.dismiss();
          options?.onClose?.();
        }}
        onSuccess={() => {
          dialogInstance.dismiss();
          options?.onSuccess?.();
        }}
        skipSubmit={options?.skipSubmit}
      />
    ),
    onDismissCallback: options?.onClose,
  });

  return dialogInstance;
};